import React from 'react';
import { cn } from '../../../lib/utils';
import { MapPin, Clock, PencilLine } from 'lucide-react';
import TableDescription from './TableDescription';
import EditableDescriptionField from './EditableDescriptionField';

const SchemaTab = ({ schema, summary, isFullScreen = false, compact = false, onUpdateMetadata, spatialColumns, temporalColumns, summaryOutside = false }) => {

  const cellPad = compact ? 'px-3 py-2' : 'px-4 py-4';
  const headPad = compact ? 'px-3 py-2' : 'px-4 py-3';
  const descCellPad = compact ? 'py-2 px-3 text-xs' : isFullScreen ? 'py-5 px-8 text-sm' : 'py-2 px-4 text-xs';

  // Helper function to get column metadata information
  const getColumnMetadata = (columnName) => {
    const metadata = {
      spatial: null,
      temporal: null
    };

    // Check spatial columns (handle both old format - array of strings - and new format - array of objects)
    const spatialCols = spatialColumns || [];
    const spatialEntry = spatialCols.find(col => {
      if (typeof col === 'string') {
        return col === columnName;
      } else if (col && typeof col === 'object') {
        return col.name === columnName || col.lat_column === columnName || col.lon_column === columnName;
      }
      return false;
    });

    if (spatialEntry) {
      if (typeof spatialEntry === 'string') {
        metadata.spatial = { type: 'spatial', display: 'Spatial Column' };
      } else if (spatialEntry && typeof spatialEntry === 'object') {
        if (spatialEntry.type === 'h3') {
          metadata.spatial = {
            type: 'h3',
            resolution: spatialEntry.resolution,
            display: `H3 Hex (Resolution ${spatialEntry.resolution})`
          };
        } else if (spatialEntry.type === 'lat_lon') {
          if (spatialEntry.lat_column === columnName) {
            metadata.spatial = {
              type: 'lat_lon',
              role: 'lat',
              pairColumn: spatialEntry.lon_column,
              display: `Latitude (paired with ${spatialEntry.lon_column})`
            };
          } else if (spatialEntry.lon_column === columnName) {
            metadata.spatial = {
              type: 'lat_lon',
              role: 'lon',
              pairColumn: spatialEntry.lat_column,
              display: `Longitude (paired with ${spatialEntry.lat_column})`
            };
          } else {
            metadata.spatial = {
              type: 'lat_lon',
              display: `Lat/Lon Pair (${spatialEntry.lat_column}, ${spatialEntry.lon_column})`
            };
          }
        } else if (spatialEntry.type === 'x_y') {
          if (spatialEntry.lat_column === columnName) {
            metadata.spatial = {
              type: 'x_y',
              role: 'x',
              pairColumn: spatialEntry.lon_column,
              display: `X Coordinate (paired with ${spatialEntry.lon_column})`
            };
          } else if (spatialEntry.lon_column === columnName) {
            metadata.spatial = {
              type: 'x_y',
              role: 'y',
              pairColumn: spatialEntry.lat_column,
              display: `Y Coordinate (paired with ${spatialEntry.lat_column})`
            };
          } else {
            metadata.spatial = {
              type: 'x_y',
              display: `X/Y Pair (${spatialEntry.lat_column}, ${spatialEntry.lon_column})`
            };
          }
        } else if (spatialEntry.type === 'wkt') {
          metadata.spatial = {
            type: 'wkt',
            display: 'WKT Geometry'
          };
        } else if (spatialEntry.type === 'geometry') {
          metadata.spatial = {
            type: 'geometry',
            display: 'Geometry'
          };
        } else {
          metadata.spatial = {
            type: 'spatial',
            display: 'Spatial Column'
          };
        }
      }
    }

    // Check temporal columns (handle both old format - array of strings - and new format - array of objects)
    const temporalCols = temporalColumns || [];
    const temporalEntry = temporalCols.find(col => {
      if (typeof col === 'string') {
        return col === columnName;
      } else if (col && typeof col === 'object') {
        return col.name === columnName;
      }
      return false;
    });

    if (temporalEntry) {
      if (typeof temporalEntry === 'string') {
        metadata.temporal = { type: 'temporal', display: 'Temporal Column' };
      } else if (temporalEntry && typeof temporalEntry === 'object' && temporalEntry.type) {
        const typeLabels = {
          'date': 'Date',
          'timestamp': 'Timestamp',
          'time': 'Time',
          'datetime': 'DateTime'
        };
        metadata.temporal = {
          type: temporalEntry.type,
          display: typeLabels[temporalEntry.type] || temporalEntry.type.charAt(0).toUpperCase() + temporalEntry.type.slice(1)
        };
      } else {
        metadata.temporal = { type: 'temporal', display: 'Temporal Column' };
      }
    }

    return metadata;
  };

  const handleSaveColumnDescription = (columnName, description) => {
    const newSchema = schema.map(col =>
      col.name === columnName ? { ...col, description } : col
    );
    onUpdateMetadata({ schema: newSchema });
  };

  if (!schema) return null;

  return (
    <div className={cn(!summaryOutside && 'h-full flex flex-col')}>
      {!summaryOutside && (summary || isFullScreen) && (
        <div className={cn(
          "border-b border-gray-100 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-900/10",
          isFullScreen ? "p-8" : "p-4"
        )}>
          <TableDescription
            summary={summary}
            isFullScreen={isFullScreen}
            onUpdateMetadata={onUpdateMetadata}
          />
        </div>
      )}

      <div className={cn(!summaryOutside && 'flex-1 overflow-hidden')}>
        <div className={cn(!summaryOutside && 'h-full overflow-auto custom-scrollbar')}>
          <table className={cn('w-full text-left border-collapse', compact ? 'min-w-[520px]' : 'min-w-[600px]')}>
            <thead className="sticky top-0 bg-white dark:bg-gray-950 z-10">
              <tr className="border-b border-gray-100 dark:border-gray-800 shadow-sm">
                {['Column', 'Type', 'Metadata', 'Description'].map((heading) => (
                  <th key={heading} className={cn(headPad, 'text-left')}>
                    <span
                      className={cn(
                        'font-black text-gray-400 uppercase',
                        compact ? 'text-[9px] tracking-wider' : 'text-[10px] tracking-[0.2em]',
                        heading === 'Description' && isFullScreen && 'inline-flex items-center gap-1'
                      )}
                    >
                      {heading}
                      {heading === 'Description' && isFullScreen && (
                        <PencilLine size={compact ? 10 : 11} className="text-emerald-500/70" aria-hidden />
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50/50 dark:divide-gray-900/20">
              {schema.map((column) => {
                const metadata = getColumnMetadata(column.name);
                return (
                <tr key={column.name} className="group hover:bg-gray-50/50 dark:hover:bg-emerald-900/5 transition-colors">
                  <td className={cn(cellPad, 'whitespace-nowrap')}>
                    <span className={cn(
                      'font-bold text-gray-900 dark:text-white font-mono tracking-tight',
                      compact ? 'text-[11px]' : 'text-xs'
                    )}>
                      {column.name}
                    </span>
                  </td>
                  <td className={cn(cellPad, 'whitespace-nowrap')}>
                    <span className={cn(
                      'font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded uppercase tracking-tight',
                      compact ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-1 rounded-lg'
                    )}>
                      {column.type}
                    </span>
                  </td>
                  <td className={cellPad}>
                    {(metadata.spatial || metadata.temporal) ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {metadata.spatial && (
                          <div className={cn(
                            'flex items-center rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                            compact ? 'gap-1 px-1.5 py-0.5' : 'gap-1.5 px-2 py-1'
                          )}>
                            <MapPin size={compact ? 10 : 12} className="flex-shrink-0" />
                            <span className={cn('font-semibold', compact ? 'text-[9px]' : 'text-[10px]')}>
                              {metadata.spatial.display}
                            </span>
                          </div>
                        )}
                        {metadata.temporal && (
                          <div className={cn(
                            'flex items-center rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                            compact ? 'gap-1 px-1.5 py-0.5' : 'gap-1.5 px-2 py-1'
                          )}>
                            <Clock size={compact ? 10 : 12} className="flex-shrink-0" />
                            <span className={cn('font-semibold', compact ? 'text-[9px]' : 'text-[10px]')}>
                              {metadata.temporal.display}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className={cn('text-gray-500 dark:text-gray-400 font-medium', descCellPad)}>
                    {isFullScreen ? (
                      <EditableDescriptionField
                        value={column.description}
                        onSave={(description) => handleSaveColumnDescription(column.name, description)}
                        multiline
                        compact={compact}
                        emptyLabel="No description available"
                      />
                    ) : (
                      <span>{column.description || 'No description available'}</span>
                    )}
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SchemaTab;
